// src/pages/enquiries/EnquiryDetail.js
// ZENO ERP — Enquiry Detail + Edit + Remarks + Convert
// Convert flow:
//   1. Check client exists by phone + name (both required)
//   2. If not → auto-create client doc
//   3. Create Service / Project / AMC from enquiry data
//   4. Mark enquiry converted: true

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc, getDoc, updateDoc, addDoc,
  collection, getDocs, query, where, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { createServiceTask } from "../services/serviceTaskService";
import {
  notifyEnquiryConverted,
  markNotificationReadByReference,
} from "../../services/notificationService";
import { fetchSitesForClient, autoSelectSiteIfOnlyOne } from "../../services/clientService";
import QuickCreateSite  from "../../components/quickcreate/forms/QuickCreateSite";
import QuickCreateModal from "../../components/quickcreate/QuickCreateModal";

const ENQUIRY_TYPES   = ["Installation", "AMC", "Service", "Repair", "Other"];
const ENQUIRY_SOURCES = ["Phone", "Reference", "Walk-in", "Website", "WhatsApp"];
const ENQUIRY_STATUS  = ["New", "In Discussion", "Quoted", "Won", "Lost"];

const STATUS_UI = {
  "New":            { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" },
  "In Discussion":  { bg: "#fef9c3", color: "#854d0e", border: "#fcd34d" },
  "Quoted":         { bg: "#ede9fe", color: "#5b21b6", border: "#a78bfa" },
  "Won":            { bg: "#dcfce7", color: "#166534", border: "#6ee7b7" },
  "Lost":           { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" },
};

export default function EnquiryDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const user     = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";

  const [enquiry,      setEnquiry]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [editing,      setEditing]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [editForm,     setEditForm]     = useState({});
  const [remarkText,   setRemarkText]   = useState("");
  const [addingRemark, setAddingRemark] = useState(false);
  const [converting,   setConverting]   = useState(""); // "service"|"project"|"amc"|"quotation"|""
  const [users,        setUsers]        = useState([]);

  // ── Site resolution for Service/AMC convert (inline, no separate page) ──
  // Project convert already goes through ProjectCreate.jsx's mandatory site
  // field. Service and AMC create directly from here — this fills the same
  // gap: 0 sites -> add one inline; 2+ sites -> pick one; exactly 1 -> auto.
  const [pendingConvert,   setPendingConvert]   = useState(null); // { type: "service"|"amc", clientData }
  const [siteOptions,      setSiteOptions]      = useState([]);
  const [selectedSiteId,   setSelectedSiteId]   = useState("");
  const [checkingSites,    setCheckingSites]    = useState(false);
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);

  useEffect(() => {
    fetchEnquiry();
    fetchUsers();
    markNotificationReadByReference(id, "enquiry_followup", companyId); // resolve on open
  }, [id]); // eslint-disable-line

  const fetchEnquiry = async () => {
    setLoading(true);
    const snap = await getDoc(doc(db, "enquiries", id));
    if (snap.exists()) {
      const data = { id: snap.id, ...snap.data() };
      if (data.company_id !== companyId) {
        alert("Access denied — this enquiry belongs to a different company");
        navigate("/enquiries");
        setLoading(false);
        return;
      }
      setEnquiry(data);
      setEditForm({
        client_name:      data.client_name || "",
        phone:            data.phone || "",
        email:            data.email || "",
        location:         data.location || "",
        enquiry_type:     data.enquiry_type || "Installation",
        source:           data.source || "Phone",
        description:      data.description || "",
        assigned_to_name: data.assigned_to_name || "",
        follow_up_date:   data.follow_up_date || "",
        estimated_value:  data.estimated_value || "",
        status:           data.status || "New",
      });
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const snap = await getDocs(query(
        collection(db, "users"),
        where("company_id", "==", companyId)
      ));
      const EXCLUDED = ["technician", "tech", "store", "driver"];
      setUsers(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(u => !EXCLUDED.includes((u.role || "").toLowerCase()))
      );
    } catch (err) { console.error("fetchUsers:", err); }
  };

  // ── Save edit ──────────────────────────────────────────
  const handleSave = async () => {
    if (!editForm.client_name.trim()) { alert("Client name required"); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, "enquiries", id), {
        ...editForm,
        estimated_value: editForm.estimated_value ? Number(editForm.estimated_value) : 0,
        updated_at:      serverTimestamp(),
        updated_by:      user.uid || user.id || "",
        updated_by_name: user.name || "",
      });
      setEditing(false);
      fetchEnquiry();
    } catch (err) {
      alert(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ── Quick status change ────────────────────────────────
  const handleStatusChange = async (newStatus) => {
    try {
      await updateDoc(doc(db, "enquiries", id), {
        status:          newStatus,
        updated_at:      serverTimestamp(),
        updated_by:      user.uid || user.id || "",
        updated_by_name: user.name || "",
      });
      fetchEnquiry();
    } catch (err) {
      alert(err.message || "Status update failed");
    }
  };

  // ── Add remark ─────────────────────────────────────────
  const handleAddRemark = async () => {
    if (!remarkText.trim()) return;
    setAddingRemark(true);
    try {
      const existing = enquiry.remarks_log || [];
      await updateDoc(doc(db, "enquiries", id), {
        remarks_log: [...existing, {
          text:    remarkText.trim(),
          by:      user.uid || user.id || "",
          by_name: user.name || "",
          at:      new Date().toISOString(),
        }],
        updated_at: serverTimestamp(),
      });
      setRemarkText("");
      fetchEnquiry();
    } catch (err) {
      alert(err.message || "Failed to add remark");
    } finally {
      setAddingRemark(false);
    }
  };

  // ── CORE: Find or create client ────────────────────────
  // Constitution rule: match by phone + name BOTH required
  // Site rule: sites collection is SOT — never write embedded client.sites[] array.
  // Site is only created if enquiry.location was genuinely provided — never fall
  // back to client_name as the site name (that fallback caused client/site name
  // confusion bugs in production).
  const findOrCreateClient = async () => {
    const snap = await getDocs(query(
      collection(db, "clients"),
      where("company_id", "==", companyId),
      where("contact",    "==", enquiry.phone.trim())
    ));

    // Name match among phone results
    const existing = snap.docs.find(d =>
      (d.data().client_name || "").toLowerCase().trim() ===
      enquiry.client_name.toLowerCase().trim()
    );

    if (existing) {
      return { client_id: existing.id, client_name: existing.data().client_name, is_new: false };
    }

    // Auto-create client — matching ClientForm.js schema (no embedded sites array)
    const clientRef = await addDoc(collection(db, "clients"), {
      company_id:           companyId,
      client_name:          enquiry.client_name.trim(),
      contact:              enquiry.phone.trim(),
      email:                enquiry.email || "",
      created_at:           serverTimestamp(),
      created_by:           user.uid || user.id || "",
      created_from_enquiry: true,
      enquiry_id:           id,
    });

    // Site — only created if location was genuinely provided.
    // Never defaults to client_name — that was the root cause of
    // client/site name confusion (e.g. "Arpit Rathi" client ending up
    // with a site literally named "Arpit Rathi").
    let site_id = "";
    if (enquiry.location && enquiry.location.trim()) {
      const siteRef = await addDoc(collection(db, "sites"), {
        company_id:           companyId,
        client_id:            clientRef.id,
        client_name:          enquiry.client_name.trim(),
        site_name:            enquiry.location.trim(),
        address:              enquiry.location.trim(),
        location_link:        "",
        lat:                  null,
        lng:                  null,
        created_at:           serverTimestamp(),
        created_by:           user.uid || user.id || "",
        created_by_name:      user.name || "",
        created_from_enquiry: true,
      });
      site_id = siteRef.id;
    }

    return {
      client_id:   clientRef.id,
      client_name: enquiry.client_name.trim(),
      site_id,
      is_new: true,
    };
  };

  // ── Mark enquiry converted ─────────────────────────────
  const markConverted = async (converted_to, converted_id) => {
    await updateDoc(doc(db, "enquiries", id), {
      converted:    true,
      converted_to,
      converted_id,
      converted_at: serverTimestamp(),
      converted_by: user.uid || user.id || "",
      status:       "Won",
      updated_at:   serverTimestamp(),
    });
  };

  // ── Site resolution — shared by Service + AMC convert ──────────────────
  const startSiteResolution = async (type, clientData) => {
    setCheckingSites(true);
    try {
      const sites = await fetchSitesForClient(companyId, clientData.client_id);
      const only  = autoSelectSiteIfOnlyOne(sites);
      if (only) {
        setCheckingSites(false);
        await proceedWithSite(type, clientData, only.id, only.site_name);
        return;
      }
      setSiteOptions(sites);
      setSelectedSiteId("");
      setPendingConvert({ type, clientData });
      setCheckingSites(false);
      if (sites.length === 0) setShowAddSiteModal(true);
    } catch (err) {
      setCheckingSites(false);
      alert("Failed to check client sites: " + err.message);
      setConverting("");
    }
  };

  const proceedWithSite = async (type, clientData, site_id, site_name) => {
    if (type === "service") await proceedConvertToService(clientData, site_id, site_name);
    else if (type === "amc") await proceedConvertToAMC(clientData, site_id, site_name);
  };

  const handleConfirmSitePicker = async () => {
    if (!selectedSiteId) { alert("Please select a site"); return; }
    const site = siteOptions.find(s => s.id === selectedSiteId);
    const { type, clientData } = pendingConvert;
    setPendingConvert(null);
    await proceedWithSite(type, clientData, site.id, site.site_name);
  };

  const handleCancelSiteFlow = () => {
    setPendingConvert(null);
    setShowAddSiteModal(false);
    setConverting("");
  };

  // ── CONVERT TO SERVICE ─────────────────────────────────
  const handleConvertToService = async () => {
    if (converting) return;
    setConverting("service");
    try {
      const clientData = await findOrCreateClient();
      await startSiteResolution("service", clientData);
    } catch (err) {
      console.error("Convert to service error:", err);
      alert(err.message || "Conversion failed");
      setConverting("");
    }
  };

  const proceedConvertToService = async (clientData, site_id, site_name) => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const result = await createServiceTask({
        company_id:      companyId,
        client_id:       clientData.client_id,
        client_name:     clientData.client_name,
        site_id,
        site_name,
        complaint:       enquiry.description,
        address:         enquiry.location || "",
        location_link:   "",
        scheduled_date:  today,
        scheduled_time:  "",
        created_by:      user.uid || user.id || "",
        created_by_name: user.name || "",
      });

      await markConverted("service", result.task_id);
      await notifyEnquiryConverted({
        enquiry_id:   id,
        enquiry_no:   enquiry.enquiry_no,
        client_name:  enquiry.client_name,
        converted_to: "Service",
        company_id:   companyId,
      });
      alert(`✅ Service created${clientData.is_new ? " + New client added to Client List" : ""}`);
      fetchEnquiry();
      navigate(`/services/${result.task_id}`);
    } catch (err) {
      console.error("Convert to service error:", err);
      alert(err.message || "Conversion failed");
    } finally {
      setConverting("");
    }
  };

  // ── CONVERT TO PROJECT ─────────────────────────────────
  const handleConvertToProject = async () => {
    if (converting) return;
    setConverting("project");
    try {
      // ✅ Client find/create karo — ID chahiye pre-fill ke liye
      const { client_id, client_name } = await findOrCreateClient();

      // ✅ ProjectCreate form pe navigate karo with pre-filled data
      // Wahan SM site, project_type (VRF/Chiller), checklist select karega
      navigate("/projects/create", {
        state: {
          from_enquiry:  true,
          enquiry_id:    id,
          client_id,
          client_name,
          project_name:  `${client_name} — ${enquiry.enquiry_type || "Installation"}`,
          description:   enquiry.description  || "",
          total_value:   enquiry.estimated_value || "",
          project_type:  enquiry.enquiry_type?.toLowerCase() || "installation",
        }
      });
    } catch (err) {
      console.error("Convert to project error:", err);
      alert(err.message || "Conversion failed");
    } finally {
      setConverting("");
    }
  };

  // ── CONVERT TO AMC ─────────────────────────────────────
  const handleConvertToAMC = async () => {
    if (converting) return;
    setConverting("amc");
    try {
      const clientData = await findOrCreateClient();
      await startSiteResolution("amc", clientData);
    } catch (err) {
      console.error("Convert to AMC error:", err);
      alert(err.message || "Conversion failed");
      setConverting("");
    }
  };

  const proceedConvertToAMC = async (clientData, site_id, site_name) => {
    try {
      const amcSnap = await getDocs(query(
        collection(db, "amc_contracts"),
        where("company_id", "==", companyId)
      ));
      const contract_no = `AMC-${String(amcSnap.size + 1).padStart(3, "0")}`;
      const today     = new Date().toISOString().slice(0, 10);
      const oneYear   = new Date();
      oneYear.setFullYear(oneYear.getFullYear() + 1);
      const end_date  = oneYear.toISOString().slice(0, 10);

      const amcRef = await addDoc(collection(db, "amc_contracts"), {
        company_id:           companyId,
        contract_no,
        billing_type:         "per_site",
        created_from_project: false,
        created_from_enquiry: true,
        enquiry_id:           id,
        project_id:           "",
        project_name:         "",
        client_id:            clientData.client_id,
        client_name:          clientData.client_name,
        site_id,
        site_name,
        description:          enquiry.description || `AMC — ${clientData.client_name}`,
        amount:               Number(enquiry.estimated_value || 0),
        frequency:            "yearly",
        start_date:           today,
        end_date,
        next_invoice_date:    today,
        status:               "active",
        last_invoice_id:      "",
        last_invoice_date:    "",
        created_by:           user.uid || user.id || "",
        created_by_name:      user.name || "",
        created_at:           serverTimestamp(),
        updated_at:           serverTimestamp(),
      });

      await markConverted("amc", amcRef.id);
      await notifyEnquiryConverted({
        enquiry_id:   id,
        enquiry_no:   enquiry.enquiry_no,
        client_name:  enquiry.client_name,
        converted_to: "AMC",
        company_id:   companyId,
      });
      alert(`✅ AMC ${contract_no} created${clientData.is_new ? " + New client added to Client List" : ""}\nReview amount/dates in AMC Contracts`);
      fetchEnquiry();
      navigate("/amc-contracts");
    } catch (err) {
      console.error("Convert to AMC error:", err);
      alert(err.message || "Conversion failed");
    } finally {
      setConverting("");
    }
  };

  // ── QUOTATION ──────────────────────────────────────────
  const handleCreateQuotation = async () => {
    if (converting) return;
    setConverting("quotation");
    try {
      const { client_id } = await findOrCreateClient();
      navigate("/quotations/create", {
        state: {
          from_enquiry: true,
          enquiry_id:   id,
          client_id:    client_id || "",
          client_name:  enquiry.client_name || "",
          client_phone: enquiry.phone        || "",
          client_email: enquiry.email        || "",
          site_name:    enquiry.location     || "",
          subject:      enquiry.description  || "",
          project_type: enquiry.enquiry_type?.toLowerCase() || "installation",
        }
      });
    } catch (err) {
      console.error("Quotation nav error:", err);
      alert(err.message || "Failed");
    } finally {
      setConverting("");
    }
  };

  if (loading)  return <p className="p-4 text-slate-400">Loading...</p>;
  if (!enquiry) return <p className="p-4 text-red-500">Enquiry not found</p>;

  const ui = STATUS_UI[enquiry.status] || STATUS_UI["New"];
  const isQuoted = enquiry.status === "Quoted";
  const isWon    = enquiry.status === "Won";

  return (
    <div className="p-4 pb-28 bg-gray-50 min-h-screen max-w-2xl mx-auto space-y-4">

      <button onClick={() => navigate("/enquiries")}
        className="text-sm text-slate-500 hover:text-slate-800">
        ← Back
      </button>

      {/* ── HEADER CARD ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4"
        style={{ borderLeft: `4px solid ${ui.border}` }}>
        <div className="flex justify-between items-start">
          <div>
            <p className="font-bold text-slate-800 text-lg">{enquiry.client_name}</p>
            <p className="text-sm text-slate-500">{enquiry.enquiry_no} • {enquiry.phone}</p>
            {enquiry.email    && <p className="text-xs text-slate-400">{enquiry.email}</p>}
            {enquiry.location && <p className="text-xs text-slate-400">📍 {enquiry.location}</p>}
          </div>
          <span className="text-xs font-bold px-3 py-1 rounded-full"
            style={{ background: ui.bg, color: ui.color }}>
            {enquiry.status}
          </span>
        </div>

        {/* Converted badge */}
        {enquiry.converted && (
          <div className="mt-3 px-3 py-2 bg-green-50 border border-green-200 rounded-xl text-xs text-green-700 font-semibold">
            ✅ Converted to {enquiry.converted_to?.toUpperCase()} —{" "}
            {enquiry.converted_at?.seconds
              ? new Date(enquiry.converted_at.seconds * 1000).toLocaleDateString("en-IN")
              : ""}
          </div>
        )}

        {/* Quick status buttons */}
        {!enquiry.converted && (
          <div className="flex flex-wrap gap-2 mt-3">
            {ENQUIRY_STATUS.map(s => (
              <button key={s}
                onClick={() => handleStatusChange(s)}
                disabled={enquiry.status === s}
                className={`text-xs px-3 py-1 rounded-full border font-semibold transition ${
                  enquiry.status === s
                    ? "opacity-40 cursor-default border-slate-200 text-slate-400"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}>
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── DETAILS CARD ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex justify-between items-center mb-3">
          <p className="text-xs font-bold text-slate-500 uppercase">Details</p>
          {!enquiry.converted && (
            <button onClick={() => setEditing(!editing)}
              className="text-xs text-blue-600 font-semibold border border-blue-200 px-3 py-1 rounded-xl hover:bg-blue-50">
              {editing ? "Cancel" : "✏️ Edit"}
            </button>
          )}
        </div>

        {!editing ? (
          <div className="space-y-2 text-sm">
            {[
              ["Type",        enquiry.enquiry_type],
              ["Source",      enquiry.source],
              ["Assigned To", enquiry.assigned_to_name || "—"],
              ["Follow-up",   enquiry.follow_up_date   || "—"],
              ["Est. Value",  enquiry.estimated_value > 0
                ? `₹${Number(enquiry.estimated_value).toLocaleString("en-IN")}` : "—"],
            ].map(([label, val]) => (
              <div key={label} className="flex justify-between">
                <span className="text-slate-400">{label}</span>
                <span className="font-semibold text-slate-700">{val}</span>
              </div>
            ))}
            <div className="mt-2 pt-2 border-t border-slate-100">
              <p className="text-xs text-slate-400 mb-1">Requirement</p>
              <p className="text-slate-700">{enquiry.description}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Client Name *</label>
                <input value={editForm.client_name}
                  onChange={e => setEditForm(f => ({ ...f, client_name: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Phone</label>
                <input value={editForm.phone}
                  onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Email</label>
                <input value={editForm.email}
                  onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Location</label>
                <input value={editForm.location}
                  onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Type</label>
                <select value={editForm.enquiry_type}
                  onChange={e => setEditForm(f => ({ ...f, enquiry_type: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none bg-white">
                  {ENQUIRY_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Source</label>
                <select value={editForm.source}
                  onChange={e => setEditForm(f => ({ ...f, source: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none bg-white">
                  {ENQUIRY_SOURCES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Assigned To</label>
                <select value={editForm.assigned_to_name}
                  onChange={e => setEditForm(f => ({ ...f, assigned_to_name: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none bg-white">
                  <option value="">— Select —</option>
                  {users.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Follow-up Date</label>
                <input type="date" value={editForm.follow_up_date}
                  onChange={e => setEditForm(f => ({ ...f, follow_up_date: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Est. Value ₹</label>
              <input type="number" min="0" value={editForm.estimated_value}
                onChange={e => setEditForm(f => ({ ...f, estimated_value: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl p-2.5 text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Requirement</label>
              <textarea rows={3} value={editForm.description}
                onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl p-2.5 text-sm resize-none outline-none" />
            </div>
            <button onClick={handleSave} disabled={saving}
              className="w-full py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm disabled:opacity-40">
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}
      </div>

      {/* ── CONVERT ACTIONS ── */}
      {(isWon || isQuoted) && !enquiry.converted && (
        <div className={`border rounded-2xl p-4 ${
          isQuoted ? "bg-purple-50 border-purple-200" : "bg-green-50 border-green-200"
        }`}>
          <p className={`text-xs font-bold uppercase mb-1 ${
            isQuoted ? "text-purple-700" : "text-green-700"
          }`}>
            {isQuoted ? "📝 Quoted — Actions" : "🎉 Enquiry Won — Convert To"}
          </p>
          <p className="text-xs text-slate-500 mb-3">
            Client will be auto-created in Client List if not already exists.
          </p>
          <div className="flex gap-2 flex-wrap">
            {isWon && (
              <>
                <button onClick={handleConvertToService} disabled={!!converting}
                  className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40">
                  {converting === "service" ? (checkingSites ? "Checking site..." : "Creating...") : "🔧 Service"}
                </button>
                <button onClick={handleConvertToProject} disabled={!!converting}
                  className="flex-1 py-2.5 rounded-xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-40">
                  {converting === "project" ? "Creating..." : "📁 Project"}
                </button>
                <button onClick={handleConvertToAMC} disabled={!!converting}
                  className="flex-1 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-40">
                  {converting === "amc" ? (checkingSites ? "Checking site..." : "Creating...") : "📋 AMC"}
                </button>
              </>
            )}
            <button onClick={handleCreateQuotation} disabled={!!converting}
              className="flex-1 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 disabled:opacity-40">
              {converting === "quotation" ? "Opening..." : "💰 Quotation"}
            </button>
          </div>
        </div>
      )}

      {/* ── REMARKS LOG ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <p className="text-xs font-bold text-slate-500 uppercase mb-3">Remarks / Follow-up Log</p>

        <div className="flex gap-2 mb-4">
          <textarea rows={2} placeholder="Add a remark or follow-up note..."
            value={remarkText} onChange={e => setRemarkText(e.target.value)}
            className="flex-1 border border-slate-200 rounded-xl p-3 text-sm resize-none outline-none focus:ring-2 focus:ring-cyan-300" />
          <button onClick={handleAddRemark} disabled={addingRemark || !remarkText.trim()}
            className="px-4 py-2 rounded-xl bg-slate-700 text-white text-sm font-semibold disabled:opacity-40 self-end">
            {addingRemark ? "..." : "Add"}
          </button>
        </div>

        {(!enquiry.remarks_log || enquiry.remarks_log.length === 0) ? (
          <p className="text-xs text-slate-400 text-center py-4">No remarks yet</p>
        ) : (
          <div className="space-y-2">
            {[...enquiry.remarks_log].reverse().map((r, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                <p className="text-sm text-slate-700">{r.text}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {r.by_name || "—"} • {r.at?.slice(0, 10) || "—"}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── SITE PICKER (client has 2+ sites) ── */}
      {pendingConvert && !showAddSiteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5">
            <h3 className="font-bold text-slate-800 mb-1">Select a Site</h3>
            <p className="text-xs text-slate-500 mb-3">
              This client has multiple sites — pick the one this {pendingConvert.type === "amc" ? "AMC" : "service job"} is for.
            </p>
            <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
              {siteOptions.map(s => (
                <label key={s.id}
                  className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer text-sm ${
                    selectedSiteId === s.id ? "border-blue-400 bg-blue-50" : "border-slate-200"
                  }`}>
                  <input type="radio" name="site_pick" checked={selectedSiteId === s.id}
                    onChange={() => setSelectedSiteId(s.id)} />
                  <span className="text-slate-700">{s.site_name}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={handleCancelSiteFlow}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm">
                Cancel
              </button>
              <button onClick={handleConfirmSitePicker}
                className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm">
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD SITE (client has 0 sites) ── */}
      <QuickCreateModal
        open={showAddSiteModal}
        onClose={handleCancelSiteFlow}
        title="Add a Site for This Client"
      >
        {pendingConvert && (
          <QuickCreateSite
            companyId={companyId}
            clientId={pendingConvert.clientData.client_id}
            clientName={pendingConvert.clientData.client_name}
            onCreated={async (newSite) => {
              setShowAddSiteModal(false);
              const { type, clientData } = pendingConvert;
              setPendingConvert(null);
              await proceedWithSite(type, clientData, newSite.id, newSite.site_name);
            }}
            onCancel={handleCancelSiteFlow}
          />
        )}
      </QuickCreateModal>

      {/* ── META ── */}
      <div className="text-xs text-slate-400 text-center pb-4">
        Created by {enquiry.created_by_name || "—"} •{" "}
        {enquiry.created_at?.seconds
          ? new Date(enquiry.created_at.seconds * 1000).toLocaleDateString("en-IN")
          : "—"}
      </div>
    </div>
  );
}